import { Component } from '@theme/component';

/**
 * Full-screen age verification gate.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog
 * @property {HTMLButtonElement} confirmButton
 * @property {HTMLButtonElement} [declineButton]
 * @property {HTMLElement} [actions]
 * @property {HTMLParagraphElement} [declineMessage]
 *
 * @extends {Component<Refs>}
 */
class AgeGateComponent extends Component {
  requiredRefs = ['dialog', 'confirmButton'];

  connectedCallback() {
    super.connectedCallback();

    this.refs.dialog.addEventListener('cancel', (event) => {
      event.preventDefault();
    });

    if (this.#isDesignMode) {
      this.#open();
      return;
    }

    if (this.#isVerified()) {
      this.#dismiss({ persist: false });
      return;
    }

    this.#lock();
    this.#open();
  }

  confirm() {
    this.#dismiss({ persist: !this.#isDesignMode });
  }

  decline() {
    const url = this.getAttribute('decline-url');

    if (url) {
      window.location.assign(url);
      return;
    }

    const { dialog, actions, confirmButton, declineButton, declineMessage } = this.refs;

    dialog.classList.add('age-gate--declined');

    if (actions) actions.hidden = true;
    confirmButton?.setAttribute('hidden', '');
    declineButton?.setAttribute('hidden', '');
    confirmButton?.setAttribute('disabled', '');
    declineButton?.setAttribute('disabled', '');

    if (!declineMessage) return;

    declineMessage.hidden = false;
    declineMessage.focus();
  }

  get #isDesignMode() {
    return this.hasAttribute('design-mode');
  }

  get #storageKey() {
    return this.getAttribute('storage-key') || 'age-gate-verified';
  }

  #isVerified() {
    try {
      return localStorage.getItem(this.#storageKey) === 'true';
    } catch {
      return false;
    }
  }

  #persist() {
    try {
      localStorage.setItem(this.#storageKey, 'true');
    } catch {
      // Ignore storage failures (private mode, disabled storage, etc.)
    }
  }

  #lock() {
    document.documentElement.setAttribute('data-age-gate-locked', '');
    document.documentElement.removeAttribute('data-age-verified');
  }

  #open() {
    const { dialog } = this.refs;

    try {
      if (!dialog.open) dialog.showModal();
    } catch {
      dialog.setAttribute('open', '');
    }

    this.refs.confirmButton?.focus();
  }

  /**
   * @param {{ persist?: boolean }} [options]
   */
  #dismiss({ persist = true } = {}) {
    if (persist) this.#persist();

    document.documentElement.setAttribute('data-age-verified', '');
    document.documentElement.removeAttribute('data-age-gate-locked');

    const { dialog } = this.refs;
    if (dialog.open) dialog.close();

    this.setAttribute('hidden', '');
    this.setAttribute('inert', '');
  }
}

if (!customElements.get('age-gate-component')) {
  customElements.define('age-gate-component', AgeGateComponent);
}
